// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { PrivacyDocPublicFile, PrivacyDocsService, PROPERTY_PRIVACY_TEMPLATE, SUPPLIER_PRIVACY_TEMPLATE } from "src/app/shared/services/privacy-docs.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

type PrivacyDocPublicAccessType = "condo-owner" | "supplier";

/**
 * Public (unauthenticated) page: a visitor enters a property's tax code and
 * downloads that property's privacy document for the company identified by
 * the ":companyId" route param; the back-end validates the tax code on the
 * download call itself (@see onDownload). The same component serves both
 * the "condo-owner" and "supplier" variants, chosen via the route's
 * "docType" data.
 */
@Component({
  selector: "app-privacy-docs-public-access",
  templateUrl: "./privacyDocsPublicAccess.component.html",
  styleUrls: ["./privacyDocsPublicAccess.component.scss"],
})
export class PrivacyDocsPublicAccessComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  lang: string;

  companyId = '';
  docType: PrivacyDocPublicAccessType = "condo-owner";
  /** i18n sub-namespace matching docType ('condoOwner' | 'supplier') */
  docTypeKey = '';
  /**
   * Optional ":supplierId" route param (docType 'supplier' only): present
   * only when this page was reached via a link scoped to a specific
   * supplier, in which case it's sent alongside the "master" field's value
   * on the download call (@see onDownload)
   */
  supplierId: string | undefined;

  form: FormGroup = new FormGroup({});
  model: any = { taxCode: "" };
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};

  downloadBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: "",
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
  };

  /**
   * Class constructor
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private privacyDocsService: PrivacyDocsService,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private configService: ConfigService,
  ) {
    // This page is reached anonymously (no auth, no ":lang" route segment),
    // so it never goes through LangGuard: the language must be resolved and
    // applied here instead, same source (the "lang" cookie) as the rest of
    // the app, defaulting to configService.defaultLang for a first-time visitor
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.translateService.use(this.lang);

    const params = this.activatedRoute.snapshot.params;
    const data = this.activatedRoute.snapshot.data;
    this.companyId = params && params.companyId;
    this.docType = data && data.docType;
    this.supplierId = params && params.supplierId;

    if (!this.companyId || (this.docType !== "condo-owner" && this.docType !== "supplier")) {
      this.router.navigate([this.lang, "errors", "404"]);
      return;
    }
    this.docTypeKey = this.docType === "supplier" ? "supplier" : "condoOwner";

    this.themeService.setPageTitle(
      this.translateService.instant(`public.privacyDocsAccess.${this.docTypeKey}.pageTitle`)
    );

    this.initForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    const isSupplier = this.docType === "supplier";
    this.model = isSupplier ? { master: "", taxCode: "" } : { taxCode: "" };

    const taxCodeField: FormlyFieldConfig = {
      key: "taxCode",
      type: "input",
      templateOptions: {
        label: this.translateService.instant("public.privacyDocsAccess.taxCodeLabel"),
        required: true,
        maxLength: 20,
        // For docType 'supplier' the tax code is meaningless without a Master
        // to check it against, so it starts disabled (@see expressionProperties below)
        disabled: isSupplier,
      },
      // Formly re-evaluates this on every model change, keeping the field's
      // disabled state in sync with the "master" field's current value
      expressionProperties: isSupplier ? {
        "templateOptions.disabled": (model: any) => !model.master,
      } : undefined,
    };

    this.fields = isSupplier ? [this.buildMasterField(), taxCodeField] : [taxCodeField];
    this.downloadBtnOptions.text = this.translateService.instant("public.privacyDocsAccess.downloadButton");
  }

  /**
   * Build the "master" autocomplete field (docType 'supplier' only): choosing
   * a Master here is what enables the "taxCode" field (@see initForm) and
   * scopes the download to that Master (@see onDownload)
   *
   * @since 1.0.0
   */
  private buildMasterField(): FormlyFieldConfig {
    // Same as the "admin.company" field on property-detail2.page.ts: a
    // type-to-search ng-select backed by search$, rather than a plain
    // <select> preloaded with the full list
    const minTermLength = 0;
    return {
      key: "master",
      type: "autocomplete",
      templateOptions: {
        customLabel: this.translateService.instant("public.privacyDocsAccess.supplier.masterLabel"),
        placeholder: this.translateService.instant("public.privacyDocsAccess.supplier.masterPlaceholder"),
        required: true,
        bindLabel: "name",
        bindValue: "_id",
        multiple: false,
        hideSelected: true,
        // After picking a Master, the dropdown must not keep showing the
        // last search's stale results (@see AutocompleteTypeComponent.onChange)
        resetOnSelect: true,
        minTermLength,
        limit: 3,
        search$: (start: number, limit: number, sort: string, sortVersus: string, search: any) =>
          this.privacyDocsService.getPublicSupplierMasters(start, limit, sort, sortVersus, search, this.supplierId),
        typeToSearchText: this.translateService.instant(
          "common.words.typeToSearch",
          { minTermLength }
        ),
        clearAllText: this.translateService.instant("common.words.clearAll"),
        notFoundText: this.translateService.instant("common.words.notFound"),
        loadingText: this.translateService.instant("common.words.loading"),
      },
    };
  }

  /**
   * Handle "Scarica" action: requests the document(s) from the back-end
   * (which validates the tax code) and saves the returned file(s)
   *
   * @since 1.0.0
   */
  onDownload(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      return;
    }
    if (this.downloadBtnOptions.active) {
      return;
    }

    const docTypeUrl = this.docType === "supplier" ? SUPPLIER_PRIVACY_TEMPLATE : PROPERTY_PRIVACY_TEMPLATE;
    const masterId = this.docType === "supplier" ? this.model.master : undefined;
    const taxCode = this.model.taxCode;
    this.downloadBtnOptions.active = true;
    this.subscriptions.push(
      this.privacyDocsService.downloadPublicPropertyPrivacyDoc(this.companyId, docTypeUrl, taxCode, masterId, this.supplierId)
        .pipe(finalize(() => this.downloadBtnOptions.active = false))
        .subscribe(
          (files: PrivacyDocPublicFile[]) => {
            if (!files || !files.length) {
              this.toastrService.error(this.translateService.instant("common.errors.unknown"));
              return;
            }
            this.saveFilesSequentially(files);
            this.toastrService.success(this.translateService.instant("public.privacyDocsAccess.downloadSuccess"));
          },
          (err) => {
            // Per-IP attempts limit reached on the back-end (tax-code guessing protection)
            if (err && err.status === 429) {
              this.toastrService.error(this.translateService.instant("public.privacyDocsAccess.tooManyRequests"));
              return;
            }
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Save each of the given base64-encoded files as a Blob, one at a time:
   * most browsers block or silently drop rapid-fire simultaneous downloads
   * triggered from the same user gesture, so files after the first are
   * staggered with a short delay (@see DOWNLOAD_STAGGER_MS)
   *
   * @since 1.0.0
   */
  private saveFilesSequentially(files: PrivacyDocPublicFile[], index = 0): void {
    const file = files[index];
    FileSaver.saveAs(this.base64ToBlob(file.data, file.contentType), file.fileName);
    if (index + 1 < files.length) {
      setTimeout(() => this.saveFilesSequentially(files, index + 1), PrivacyDocsPublicAccessComponent.DOWNLOAD_STAGGER_MS);
    }
  }

  /**
   * Decode a base64 string (no "data:" URI prefix) into a Blob with the
   * given contentType
   *
   * @since 1.0.0
   */
  private base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
  }

  private static readonly DOWNLOAD_STAGGER_MS = 300;
}
